import { useState } from "react";
import {
  Box,
  Button,
  Code,
  Container,
  Heading,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { usePortalPath } from "hooks/use-portal-path";
import { apiClient } from "utils/api-client";

type RouteErrorData = {
  title?: string;
  message?: string;
};

type RouteError = {
  data?: RouteErrorData;
};

function isRouteError(error: unknown): error is RouteError {
  return typeof error === "object" && error !== null && "data" in error;
}

function isTokenExpiredError(error: unknown): boolean {
  if (!isRouteErrorResponse(error)) return false;
  try {
    const data =
      typeof error.data === "string" ? JSON.parse(error.data) : error.data;
    return data?.type === "token_expired";
  } catch {
    return false;
  }
}

export function PortalErrorScreen() {
  const isDev = import.meta.env.DEV;
  const error = useRouteError();
  const toast = useToast();
  const { isLandlordPath } = usePortalPath();
  const portalType = isLandlordPath ? "landlord" : "tenant";
  const [linkSent, setLinkSent] = useState(false);
  const routeError = isRouteError(error) ? error : null;
  const parsedData: RouteErrorData | null = (() => {
    try {
      const raw = routeError?.data;
      return typeof raw === "string" ? JSON.parse(raw) : raw ?? null;
    } catch {
      return null;
    }
  })();
  const title =
    parsedData?.title ?? "The page you are looking for does not exist.";
  const message = parsedData?.message;
  const errorStatus = isRouteErrorResponse(error) ? error.status : 404;
  const heading =
    errorStatus === 401 ? "Authorization required" : "Page not found";

  const sendLinkMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/portal/${portalType}-portal/send-link`, {}),
    onSuccess: () => {
      setLinkSent(true);
      toast({
        title: "New link sent",
        description: "Please check your email for a new access link.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: "Failed to send link",
        description: "Please try again or contact your agency.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  return (
    <Container maxW={"2xl"} pt={"32"}>
      <VStack spacing={"4"} w={"100%"}>
        <Heading>{heading}</Heading>
        <Text>{title}</Text>
        {isTokenExpiredError(error) &&
          (linkSent ? (
            <Text color={"green.500"}>
              A new link has been sent to your email.
            </Text>
          ) : (
            <Button
              colorScheme={"gray"}
              isLoading={sendLinkMutation.isPending}
              onClick={() => sendLinkMutation.mutate()}
            >
              Send new link
            </Button>
          ))}
        {isDev && (
          <Box
            borderColor={"orange.300"}
            borderWidth={2}
            w={"100%"}
            maxW={"400px"}
            bg={"orange.50"}
            borderStyle={"dashed"}
            p={4}
            borderRadius={4}
            textAlign={"center"}
            style={{
              // @ts-expect-error Exists
              textWrap: "balance",
            }}
          >
            <Text>
              {message ?? (
                <>
                  <Tag colorScheme={"orange"} bgColor={"orange.300"} mr={2}>
                    DEV
                  </Tag>
                  Check that your URL starts with a proper region prefix e.g.{" "}
                  <Code colorScheme={"orange"}>/local/{portalType}/1</Code>
                </>
              )}
            </Text>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
